import React from "react";
import { Sparkles } from "lucide-react";

/**
 * SkinSignalBadge — tiny pill shown above the product recommendations
 * when the backend's CV pipeline produced signals at high/medium
 * confidence. Communicates WHY the picks are personalised without
 * exposing raw MST/undertone internals in an evaluative way.
 *
 * Props:
 *   signals: { undertone, mst_bucket, confidence_band, beard_shadow_detected }
 *   category: string — "foundation" | "color_corrector" | "contour"
 *   cvCategories: string[] — the categories actually backed by the
 *     CV recommendation engine for this routine. Used to gate the
 *     badge — we never imply a step was CV-matched when its products
 *     were picked by the generic fallback.
 */
const SkinSignalBadge = ({ signals, category, cvCategories }) => {
  if (!signals || !signals.confidence_band) return null;
  if (!["high", "medium", "user_confirmed"].includes(signals.confidence_band)) {
    return null;
  }
  // Honesty gate: don't claim a CV match on steps whose products came
  // from the generic fallback (primer, blush, brows, mascara, ...).
  if (
    Array.isArray(cvCategories) &&
    cvCategories.length > 0 &&
    !cvCategories.includes(category)
  ) {
    return null;
  }

  // Non-evaluative, undertone-first copy. Never mentions MST numbers.
  const undertoneLabel = signals.undertone
    ? signals.undertone[0].toUpperCase() + signals.undertone.slice(1)
    : null;

  // Per-category framing — corrector gets a beard-shadow call-out,
  // foundation/contour just reference the undertone match.
  let subcopy = "Matched to your skin tone and preferences";
  if (category === "color_corrector" && signals.beard_shadow_detected) {
    subcopy = "Selected to neutralise beard shadow for your tone";
  } else if (undertoneLabel) {
    subcopy = `Chosen for ${undertoneLabel.toLowerCase()} undertones + your preferences`;
  }

  const caveat =
    signals.confidence_band === "medium"
      ? " · based on the photo and your preferences"
      : "";

  return (
    <div
      className="inline-flex items-start gap-2 text-[11px] uppercase tracking-[0.18em] text-[#9B6A6C] mb-3"
      data-testid={`skin-signal-badge-${category}`}
    >
      <Sparkles className="h-3.5 w-3.5 mt-[1px] shrink-0" />
      <span className="normal-case tracking-normal text-xs text-[#5C4B51] leading-snug">
        {subcopy}
        {caveat && (
          <span className="text-[#8A7D82] italic">{caveat}</span>
        )}
      </span>
    </div>
  );
};

export default SkinSignalBadge;
